import React, { useEffect, useRef, useCallback } from 'react';

/**
 * Component that injects raw HTML into the footer area and handles script execution properly
 * @param {string} htmlContent - Raw HTML string to inject as footer
 */
const FooterHtmlInjector = ({ htmlContent }) => {
	// Reference for the container div
	const footerContainerRef = useRef(null);
	// Track executed scripts to prevent duplicates
	const executedScriptsRef = useRef(new Set());
	
	/**
	 * Create unique hash for script content to track executed scripts
	 * @param {string} content - Script content to hash
	 * @returns {string} Hash of the content
	 */
	const createScriptHash = (content) => {
		// Simple hash function for script content
		let hash = 0;
		if (content.length === 0) return hash.toString();
		for (let i = 0; i < content.length; i++) {
			const char = content.charCodeAt(i);
			hash = ((hash << 5) - hash) + char;
			hash = hash & hash; // Convert to 32-bit integer
		}
		return Math.abs(hash).toString();
	};

	/**
	 * Load external script and return a Promise that resolves when loaded
	 * @param {string} src - Script source URL
	 * @returns {Promise} Promise that resolves when script is loaded
	 */
	const loadExternalScript = useCallback((src) => {
		return new Promise((resolve, reject) => {
			// Check if script is already loaded with our tracking attribute
			const existingScript = document.querySelector(`script[src="${src}"][data-footer-injected="true"]`);
			if (existingScript) {
				console.debug('External script already loaded:', src);
				resolve();
				return;
			}
			
			// Also check for any script with this src (including those not injected by us)
			const anyExistingScript = document.querySelector(`script[src="${src}"]`);
			if (anyExistingScript) {
				console.debug('External script exists (not tracked by footer injector):', src);
				resolve();
				return;
			}
			
			const script = document.createElement('script');
			script.src = src;
			script.async = true;
			script.setAttribute('data-footer-injected', 'true');
			script.onload = () => {
				console.debug('External script loaded:', src);
				resolve();
			};
			script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
			document.head.appendChild(script);
		});
	}, []);
	
	/**
	 * Execute inline script content with duplicate prevention
	 * @param {string} scriptContent - JavaScript code to execute
	 */
	const executeInlineScript = useCallback((scriptContent) => {
		try {
			// Create hash for the script content to track execution
			const scriptHash = createScriptHash(scriptContent);
			
			// Check if this script contains dynamic content that should be re-executed
			const isDynamicScript = scriptContent.includes('VoiceAgentClient') || 
									scriptContent.includes('initialMessage') ||
									scriptContent.includes('createSafeInstance') ||
									scriptContent.includes('.voice-ai-container');
			
			// For dynamic scripts, always execute (but clean up previous instances)
			if (isDynamicScript) {
				// Clean up previous voice agent instances
				if (window.VoiceAgentClient && typeof window.agent?.destroy === 'function') {
					try {
						window.agent.destroy();
					} catch (e) {
						console.debug('Previous agent cleanup failed:', e);
					}
				}
				
				// Clear previous agent variable
				if (window.agent) {
					delete window.agent;
				}
				
				console.debug('Executing dynamic script (Voice AI Agent)');
			} else {
				// For static scripts, skip if already executed
				if (executedScriptsRef.current.has(scriptHash)) {
					console.debug('Skipping already executed static script:', scriptHash);
					return;
				}
			}
			
			// Wrap script content in IIFE to avoid variable conflicts for non-dynamic scripts
			const wrappedContent = isDynamicScript ? scriptContent : `(function() {
				'use strict';
				${scriptContent}
			})();`;
			
			// Create a new script element for execution
			const script = document.createElement('script');
			script.textContent = wrappedContent;
			script.setAttribute('data-footer-injected', 'true');
			script.setAttribute('data-script-hash', scriptHash);
			script.setAttribute('data-script-type', isDynamicScript ? 'dynamic' : 'static');
			
			document.head.appendChild(script);
			// Remove script after execution to keep DOM clean
			document.head.removeChild(script);
			
			// Only mark static scripts as executed to prevent re-execution
			if (!isDynamicScript) {
				executedScriptsRef.current.add(scriptHash);
			}
		} catch (error) {
			console.error('Error executing inline script:', error);
		}
	}, []);
	/**
	 * Process and execute scripts in correct order
	 * @param {NodeList} scriptElements - Script elements to process
	 */
	const processScripts = useCallback(async (scriptElements) => {
		const externalScripts = [];
		const inlineScripts = [];
		
		// Separate external and inline scripts while preserving order
		scriptElements.forEach(script => {
			if (script.src) {
				externalScripts.push(script.src);
			} else if (script.textContent?.trim()) {
				inlineScripts.push(script.textContent);
			}
		});
		
		try {
			// Load all external scripts first (in parallel for better performance)
			if (externalScripts.length > 0) {
				await Promise.all(externalScripts.map(src => loadExternalScript(src)));
			}
			
			// Execute inline scripts sequentially after external scripts are loaded
			for (const scriptContent of inlineScripts) {
				executeInlineScript(scriptContent);
			}
		} catch (error) {
			console.error('Error processing scripts:', error);
		}
	}, [loadExternalScript, executeInlineScript]);
		useEffect(() => {
		// Skip if no content or container
		if (!htmlContent || !footerContainerRef.current) return;

		const container = footerContainerRef.current;

		// Clean up previous voice agent instances before setting new content
		if (window.VoiceAgentClient && typeof window.agent?.destroy === 'function') {
			try {
				window.agent.destroy();
				delete window.agent;
				console.debug('Cleaned up previous Voice AI Agent instance');
			} catch (e) {
				console.debug('Voice AI Agent cleanup failed:', e);
			}
		}

		// Set the innerHTML to inject the content
		container.innerHTML = htmlContent;

		// Extract all script elements
		const scripts = container.querySelectorAll('script');

		if (scripts.length > 0) {
			// Remove scripts from DOM to prevent automatic execution
			scripts.forEach(script => script.remove());

			// Process scripts in correct order
			processScripts(scripts);
		}

		// Cleanup function to clear executed scripts when component unmounts or content changes
		return () => {
			// Clean up voice agent when content changes or component unmounts
			if (window.VoiceAgentClient && typeof window.agent?.destroy === 'function') {
				try {
					window.agent.destroy();
					delete window.agent;
				} catch (e) {
					console.debug('Voice AI Agent cleanup in return failed:', e);
				}
			}
			
			// Clear container content
			if (container) {
				container.innerHTML = '';
			}
		};
	}, [htmlContent, processScripts]);

	// Cleanup effect when component unmounts
	useEffect(() => {
		// Copy the current Set reference to avoid stale closure issues
		const executedScripts = executedScriptsRef.current;
		
		return () => {
			// Clear executed scripts tracking when component unmounts completely
			executedScripts.clear();
		};
	}, []);

	// Render a container div that will hold the injected footer HTML
	return <div ref={footerContainerRef} data-injected-footer="true" />;
};

export default FooterHtmlInjector;
